import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterSignup from "../components/NewsletterSignup";
import TeamGallery from "../components/TeamGallery";
import { usePageMeta } from "../lib/usePageMeta";
import PostCard from "../components/PostCard";
import AdSlot from "../components/AdSlot";
import FloatingSidebarAd from "../components/FloatingSidebarAd";
import HeroCarousel from "../components/HeroCarousel";
import { Compass, ArrowRight, ShieldCheck } from "lucide-react";

export default function Home() {
  usePageMeta("Viagens Orion — Roteiros, dicas e destinos", "Viagens Orion — o guia mágico da Viagens Orion para descobrir roteiros, dicas e destinos com curadoria dos consultores Sandra e Lucas Scabio.");
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cache-buster para o hero — força bypass de cache do browser/CDN.
    // Sem isso, o carrossel principal ficava exibindo posts antigos em prod.
    const bust = Math.floor(Date.now() / 60000); // renova a cada 60s
    Promise.all([
      api.get("/posts", { params: { limit: 24, _t: bust } }),
      api.get("/categories", { params: { only_active: true } }),
    ])
      .then(([p, c]) => {
        setPosts(p.data.items || []);
        setCats(c.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const hero = posts.slice(0, 5);
  // Show ALL posts in the grid too so nothing is "hidden" only inside the carousel.
  // This ensures visitors immediately see the full editorial catalog.
  const rest = posts;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {hero.length > 0 ? (
        <HeroCarousel posts={hero} />
      ) : (
        <section className="bg-orion-purple text-white grain">
          <div className="container-orion py-24 md:py-36 max-w-3xl">
            <div className="cat-chip !text-orion-gold mb-4">Viagens Orion</div>
            <h1 className="font-serif text-5xl md:text-7xl font-semibold leading-[1.02]">
              Roteiros que começam <em className="text-orion-orange not-italic">antes</em> da mala ser feita.
            </h1>
            <p className="mt-6 text-white/80 text-lg">Os primeiros artigos chegam em breve. Enquanto isso, conheça os cuidados na hora de contratar sua viagem.</p>
            <Link to="/cuidados" className="mt-8 inline-flex items-center gap-2 border border-orion-gold text-orion-gold px-6 py-3 text-xs uppercase tracking-widest hover:bg-orion-gold hover:text-orion-purple">
              <ShieldCheck size={16} /> Cuidados ao planejar
            </Link>
          </div>
        </section>
      )}

      {/* CATEGORY STRIP - parent categories */}
      <section className="border-b border-[#EFEAE0] bg-white">
        <div className="container-orion py-5 flex gap-8 overflow-x-auto text-xs uppercase tracking-[0.22em] font-semibold text-[#2C1139]/70 no-scrollbar">
          <Compass size={14} className="text-orion-orange shrink-0 mt-[2px]" />
          {cats.filter((c) => !c.parent_slug).map((c) => (
            <Link
              key={c.id}
              to={`/categoria/${c.slug}`}
              className="whitespace-nowrap hover:text-orion-orange transition-colors"
              data-testid={`strip-cat-${c.slug}`}
            >
              {c.name}
            </Link>
          ))}
          <Link to="/cuidados" className="whitespace-nowrap hover:text-orion-orange transition-colors text-orion-orange">
            Cuidados ao planejar
          </Link>
        </div>
      </section>

      {/* GRID */}
      <main className="container-orion py-16 md:py-24 flex-1">
        {loading && <div className="text-center text-[#8A8191] py-20">Carregando…</div>}

        {!loading && posts.length === 0 && (
          <div className="text-center py-16">
            <div className="cat-chip mb-4">Em breve</div>
            <h2 className="font-serif text-3xl md:text-4xl text-orion-purple mb-4">Os primeiros roteiros estão a caminho.</h2>
            <p className="text-[#594A63] max-w-xl mx-auto">
              A Orion está preparando artigos, guias e dicas. Volte em breve — ou explore a seção de cuidados ao contratar sua viagem.
            </p>
            <Link to="/cuidados" className="mt-8 inline-flex items-center gap-2 text-orion-orange uppercase tracking-widest text-xs font-semibold">
              Ver cuidados <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {rest.length > 0 && (
          <>
            <div className="flex items-end justify-between mb-6 md:mb-8 border-b border-[#EFEAE0] pb-3 md:pb-4">
              <div>
                <div className="cat-chip mb-2">Mais recentes</div>
                <h2 className="font-serif text-xl md:text-4xl text-orion-purple">Novidades da redação</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
              {rest.slice(0, 8).map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </div>

            {/* Middle ad slot — sempre renderiza (mesmo com <= 8 posts) para o
                anúncio de combo horizontal aparecer também na home mais enxuta. */}
            <AdSlot id="home-mid" label="Publicidade · Google AdSense" height={90} />

            {rest.length > 8 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10 mt-10">
                {rest.slice(8).map((p) => (
                  <PostCard key={p.id} post={p} />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <TeamGallery
        title="Nossa Fadinha em campo"
        subtitle="Um recorte dos últimos roteiros: neve, cataratas, cachoeiras e as pessoas que a Orion levou para conhecer o Brasil de perto."
      />

      <div className="container-orion pb-16">
        <NewsletterSignup variant="editorial" />
      </div>

      <Footer />
      {/* Floating sidebar 300×600 — só aparece em ≥1280px */}
      <FloatingSidebarAd />
    </div>
  );
}
